import { ecLean, itemField, record, type LogRow, type Row } from '../ability/context.js';
import { statusScalar } from '../actions/activity.js';
import { readStatusRowStack } from '../compile/status-stack-reader.js';
import { statusItemId } from '../statuses/expiry.js';
import type { CampaignClockCols } from '../../shared/campaign-clock.js';
import type { HungerConsequencePlan } from './hunger-consequences.js';
import type { TimePushCommand } from './time-push.js';
import { applyStatusSkillGlobals } from '../compile/creature-status-skill-globals.js';
import { createStatusIntegerCompiler } from '../compile/status-integer-contribution.js';
import { C1_CONTRACT } from '../compile/possessor-compile.js';
import { compileStatusRootFields, type StatusRootFields } from '../compile/status-root-residue.js';
import { statusProjectionDependencies } from '../compile/status-attack-budget.js';
import { createStatusLayerReadContext } from '../compile/status-row-construction.js';
import { statusRows } from '../statuses/expiry.js';
import { normalizePhpListish } from '../../shared/php-listish.js';
import { readLayerListEntryUuid } from '../compile/inventory-raw-readers.js';

export interface TimeStatusProjection { roots: StatusRootFields; skills: Record<string, Row>; globals: Record<string, { value: number; sources: unknown[] }> }

/** Hunger's global skill effects already have an incremental native writer. Only its changed entries travel. */
export function timeStatusProjection(row: Row, plan: HungerConsequencePlan, definition: (id: string) => Row | null): TimeStatusProjection | undefined {
  const before = record(record(row.computed)?.creature);
  if (!before || !record(before.skills)) return undefined;
  let skills = record(before.skills), modifiers = record(before.modifiers), sources = record(before.modifier_sources);
  let target = statusRows(row, 'target');
  const dependencies = statusProjectionDependencies(row, definition);
  const integer = createStatusIntegerCompiler(dependencies);
  const view = (statuses: Row[]): Row => Object.assign(Object.create(row), { statuses: { target: statuses } });
  for (const event of plan.events) {
    const status = event.status, id = String(statusScalar(status, 'status_id')), def = definition(statusItemId(status));
    const priorTarget = target;
    if (event.kind === 'loss') target = target.filter(s => statusScalar(s, 'status_id') !== id);
    else target = [status, ...target.filter(s => statusScalar(s, 'status_id') !== id)];
    const layers = record(def?.item_layer_types) ?? {};
    if (!['skill_rolls_adv', 'skill_rolls_dis', 'skill_rolls_mod'].some(k => k in layers)) continue;
    // The skill writer consumes POST-mutation totals and attribution. Reuse the native
    // status contribution walk for only the global families this event changed.
    for (const key of ['skill_rolls_adv', 'skill_rolls_dis']) if (key in layers) {
      const prior = integer.contribution(view(priorTarget), key), next = integer.contribution(view(target), key);
      modifiers = { ...modifiers, [key]: Number(modifiers?.[key] ?? 0) - prior.total + next.total };
      sources = { ...sources, [key]: [...next.sources, ...(Array.isArray(sources?.[key])
        ? (sources[key] as Row[]).filter(source => source.kind !== 'status_effect') : [])] };
    }
    const projected = applyStatusSkillGlobals({ skills: skills ?? undefined, modifiers: modifiers ?? undefined,
      modifierSources: sources ?? undefined, layers, statusName: String(itemField(def, 'item_name') ?? ''), statusRowId: id,
      applied: event.kind !== 'loss', stacks: event.kind === 'stack' ? 1 : readStatusRowStack(status),
      skillEntries: C1_CONTRACT.rollup.skillEntries, heldStatusIds: new Set(target.map(s => String(statusScalar(s, 'status_id')))) });
    if (!projected.ok) throw new Error(projected.reason);
    skills = projected.skills; modifiers = projected.modifiers ?? null; sources = projected.modifierSources ?? null;
  }
  const read = createStatusLayerReadContext(dependencies);
  const changedItems = new Set(plan.events.map(event => statusItemId(event.status)));
  for (const bucket of ['passive_ability', 'feat']) for (const entry of normalizePhpListish(record(before.abilities)?.[bucket])) {
    const id = String(readLayerListEntryUuid(entry) ?? '').toUpperCase(), def = definition(id);
    if (!def) continue;
    for (const key of ['if_caster_has_status_effect_active', 'if_caster_has_status_effect_not_active'])
      if (Object.values(read.layerEntries(def, key)).some(value => changedItems.has(String(readLayerListEntryUuid(value) ?? '').toUpperCase())))
        throw new Error('conditional-provider:' + id);
  }
  const after: Row = Object.create(row); // Borrow the held inputs; only the changed status bucket is owned here.
  after.statuses = { ...record(row.statuses), target };
  const roots = compileStatusRootFields(row, after, { definition, entries: (host, key) => Object.values(read.layerEntries(host, key)), value: read.layerValue,
    active: () => false, buckets: () => ({}), gearLayers: [], quantityIds: C1_CONTRACT.rollup.quantityIds, gearCounted: () => false });
  const changedSkills: Record<string, Row> = {}, globals: TimeStatusProjection['globals'] = {};
  for (const key of new Set([...Object.keys(record(before.skills) ?? {}), ...Object.keys(skills ?? {})])) {
    const prior = record(record(before.skills)?.[key]) ?? {}, next = record(skills?.[key]) ?? {}, patch: Row = {};
    for (const field of new Set([...Object.keys(prior), ...Object.keys(next)]))
      if (JSON.stringify(prior[field]) !== JSON.stringify(next[field])) patch[field] = next[field] ?? null;
    if (Object.keys(patch).length) changedSkills[key] = patch;
  }
  for (const key of ['skill_rolls_adv', 'skill_rolls_dis'])
    if (JSON.stringify(record(before.modifiers)?.[key]) !== JSON.stringify(modifiers?.[key])
      || JSON.stringify(record(before.modifier_sources)?.[key]) !== JSON.stringify(sources?.[key]))
      globals[key] = { value: Number(modifiers?.[key] ?? 0), sources: Array.isArray(sources?.[key]) ? sources![key] as unknown[] : [] };
  return { roots, skills: changedSkills, globals };
}

export interface TimeSavePlan {
  writes: Array<{ kind: 'insert' | 'upsert' | 'delete' | 'stack'; actor_id: string; row: Row; before?: number }>;
  rows: LogRow[];
}
const stamp = (prefix: string, clock: CampaignClockCols): Row => Object.fromEntries(
  Object.entries(clock).map(([key, value]) => [prefix + key, value]));
const noEnd: CampaignClockCols = { year: 0, month: 0, day: 0, hour: 0, minute: 0, second: 0 };

/** Serialize the primary decisions already made by the native hunger pass. No saver derives a duration, stack or log order. */
export function timeSavePlan(command: TimePushCommand, plans: HungerConsequencePlan[],
  definition: (id: string) => Row | null, rowFor: (id: string) => Row | null): TimeSavePlan {
  const writes: TimeSavePlan['writes'] = [], rows: LogRow[] = [];
  for (const plan of plans) {
    const frame = command.frame.rows[plan.actorId]!;
    const holder = rowFor(plan.actorId);
    if (holder) if (!('status_projection' in frame)) frame.status_projection = timeStatusProjection(holder, plan, definition);
    const removed = new Set(plan.events.filter(e => e.kind === 'loss').map(e => String(statusScalar(e.status, 'status_id'))));
    for (const event of plan.events) {
      const status = event.status, id = String(statusScalar(status, 'status_id')), item = statusItemId(status);
      if (event.kind === 'loss') {
        writes.push({ kind: 'delete', actor_id: plan.actorId, row: { status_id: id, status_item_id: item } });
        if (!frame.status_remove_ids.includes(id)) frame.status_remove_ids.push(id);
        continue;
      }
      const count = readStatusRowStack(status);
      const entry: Row = { ...status, status_id: id, status_item_id: item,
        status_target_id: plan.actorId, status_arr: frame.statuses_upsert?.find(s => s.status_id === id)?.status_arr ?? status.status_arr ?? definition(item) };
      if (event.kind === 'stack') writes.push({ kind: 'stack', actor_id: plan.actorId,
        row: { status_id: id, status_item_id: item, status_stack: count },
        before: readStatusRowStack(statusRows(holder ?? {}, 'target').find(s => String(statusScalar(s, 'status_id')) === id) ?? {}) });
      else {
        const row: Row = { ...stamp('status_starts_campaign_', command.meters.find(m => m.actor_id === plan.actorId)!.tick.starts),
          ...stamp('status_ends_campaign_', noEnd), ...frame.statuses_upsert?.find(s => s.status_id === id), ...status, status_private: 0, status_stack: count };
        delete row.status_arr;
        writes.push({ kind: 'insert', actor_id: plan.actorId, row });
        Object.assign(entry, row);
      }
      if (!removed.has(id)) {
        frame.statuses_upsert ??= [];
        const at = frame.statuses_upsert.findIndex(s => s.status_id === id);
        if (at < 0) frame.statuses_upsert.push(entry); else frame.statuses_upsert[at] = entry;
      }
      const name = String(itemField(definition(item), 'item_name') ?? item);
      rows.push({ roll_type_1: event.kind === 'stack' ? 'status_stack_increased' : 'status_effect_applied',
        roll_type_2: null, roll_result: 0, roll_private: command.roll_private as 0 | 1 | 2,
        roll_item_id: item, roll_by: plan.actorId, roll_to: plan.actorId,
        rolls_full: event.kind === 'stack' ? `${name} (${count})` : name });
    }
  }
  for (const meter of command.meters) {
    const marker = command.frame.rows[meter.actor_id]!.statuses_upsert?.find(s => s.status_id === meter.tick.status_id);
    if (!marker) throw new Error('native meter marker missing');
    const row: Row = { ...marker, status_caster_id: meter.actor_id };
    delete row.status_arr;
    writes.push({ kind: 'upsert', actor_id: meter.actor_id, row });
  }
  // Labels are presentation data. The real participant snapshot is attached at the browser boundary.
  for (const row of rows) {
    const holder = rowFor(String(row.roll_by));
    if (!holder || !ecLean(holder, 'encounter_creature_id')) throw new Error('native hunger participant missing');
  }
  return { writes, rows };
}
